import { ResponseArray } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import { useQueryWithHandler } from './useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useEffect, useState, useCallback, useMemo } from 'react'
import qs from 'query-string'

export interface CustomerPreview {
    customer_id: number
    customer_email: string
    customer_name?: string
    company_name?: string
    customer_phone?: string
    service_groups?: string[]
    [key: string]: string | number | string[] | undefined
}

export interface FilterValues {
    customer_status: string[]
    customer_industry: string[]
    product_group_name: string[]
    service_status: string[]
    service_interestings: string[]
    customer_sale_followers: number[]
    customer_cs_followers: number[]
    exclude_customer_ids: number[]
    required_fields: string[]
}

const defaultFilters: FilterValues = {
    customer_status: [],
    customer_industry: [],
    product_group_name: [],
    service_status: [],
    service_interestings: [],
    customer_sale_followers: [],
    customer_cs_followers: [],
    exclude_customer_ids: [],
    required_fields: [],
}

type useCustomersPreviewFilterResponse = ResponseArray<CustomerPreview>

interface PreviewCustomersFiltersPayload {
    filters: FilterValues
    page?: number
    page_size?: number
    search?: string
}
type PreviewCustomersFiltersPayloadInput = {
    filters?: Partial<FilterValues>
    page?: number
    page_size?: number
    search?: string
}

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 10

const previewCustomersFilters = async (
    payload: PreviewCustomersFiltersPayload
) => {
    const filters = cleanFilters(payload.filters)
    const filtersBase64 = encodeFilters(filters)
    const queryParams: Record<string, unknown> = {
        filters: filtersBase64,
        page: payload.page,
        page_size: payload.page_size,
    }
    if (payload.search) {
        queryParams.q = payload.search
    }
    const response = await datacentral.get<ResponseArray<CustomerPreview>>(
        `/customers/with-services?${qs.stringify(queryParams)}`
    )
    return response.data
}

export const useCustomersPreviewFilter = () => {
    const [customersPreview, setCustomersPreview] =
        useState<useCustomersPreviewFilterResponse>({
            results: [],
            pagination: {
                total: 0,
                page: DEFAULT_PAGE,
                page_size: DEFAULT_PAGE_SIZE,
                total_pages: 0,
                page_next: 0,
                page_prev: 0,
            },
        })

    const [shouldFetch, setShouldFetch] = useState(false)
    const [filtersPayload, setFiltersPayload] =
        useState<PreviewCustomersFiltersPayload>({
            filters: { ...defaultFilters },
            page: DEFAULT_PAGE,
            page_size: DEFAULT_PAGE_SIZE,
        })
    const { data, isLoading, error } = useQueryWithHandler<
        ResponseArray<CustomerPreview>
    >({
        queryKey: [
            QUERY_KEYS.CUSTOMERS_PREVIEW_FILTER,
            filtersPayload.filters,
            filtersPayload.page,
            filtersPayload.page_size,
            filtersPayload.search,
        ],
        queryFn: () => previewCustomersFilters(filtersPayload),
        enabled: shouldFetch,
    })

    useEffect(() => {
        if (error) {
            setCustomersPreview({
                results: [],
                pagination: {
                    total: 0,
                    page: DEFAULT_PAGE,
                    page_size: DEFAULT_PAGE_SIZE,
                    total_pages: 0,
                    page_next: 0,
                    page_prev: 0,
                },
            })
            setShouldFetch(false)
        } else if (data) {
            // Ensure pagination page matches the requested page
            setCustomersPreview({
                ...data,
                pagination: {
                    total: data.pagination?.total ?? 0,
                    page: filtersPayload.page ?? DEFAULT_PAGE,
                    page_size: filtersPayload.page_size ?? DEFAULT_PAGE_SIZE,
                    total_pages: data.pagination?.total_pages ?? 0,
                    page_next: data.pagination?.page_next ?? 0,
                    page_prev: data.pagination?.page_prev ?? 0,
                },
            })
            setShouldFetch(false)
        }
    }, [error, data])

    const fetchCustomersPreview = useCallback(
        (payload?: PreviewCustomersFiltersPayloadInput, reset = false) => {
            if (payload) {
                setFiltersPayload((prev) => ({
                    ...prev,
                    ...payload,
                    filters: {
                        ...prev.filters,
                        ...(payload.filters ?? {}),
                    },
                    page: reset ? DEFAULT_PAGE : (payload.page ?? prev.page),
                    page_size: payload.page_size ?? prev.page_size,
                    search: payload.search ?? prev.search,
                }))
            }
            setShouldFetch(true)
        },
        [] // Empty deps - function doesn't depend on any props/state
    )

    const resetCustomersPreview = useCallback(() => {
        setFiltersPayload((prev) => ({
            ...prev,
            page: DEFAULT_PAGE,
        }))
        setShouldFetch(true)
    }, [])

    // Memoize return values to prevent unnecessary re-renders
    const returnValue = useMemo(
        () => ({
            customersPreview,
            isLoading,
            error,
            fetchCustomersPreview,
            resetCustomersPreview,
            currentPage: filtersPayload.page,
            currentPageSize: filtersPayload.page_size,
            currentSearch: filtersPayload.search,
        }),
        [
            customersPreview,
            isLoading,
            error,
            fetchCustomersPreview,
            filtersPayload.page,
            filtersPayload.page_size,
            filtersPayload.search,
        ]
    )

    return returnValue
}
const cleanFilters = (filters: FilterValues): Record<string, unknown> => {
    return Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value.length > 0)
    )
}
const encodeFilters = (filters: Record<string, unknown>): string => {
    if (Object.keys(filters).length === 0) return ''
    // Server expects base64-encoded JSON in query param
    return btoa(JSON.stringify(filters))
}

export const CleanEncodedFilters = (filters: FilterValues): string => {
    console.log('filters', filters)
    return btoa(
        JSON.stringify(
            Object.fromEntries(
                Object.entries(filters).filter(([_, value]) => value.length > 0)
            )
        )
    )
}
